import { component$, Slot, useContext } from "@builder.io/qwik";
import {
  Link,
  routeAction$,
  z,
  zod$,
  routeLoader$,
} from "@builder.io/qwik-city";
import { Button } from "~/components/ui/button";
import { CoachProfileImage } from "~/components/ui/coach-profile-image";
import { LuQuote } from "@qwikest/icons/lucide";
import { GradientBackground } from "~/components/ui/gradient-background";
import { Feature, hasFeature } from "~/lib/has-feature";
import { CoachDetails } from "~/components/coach-details";
import { SocialLink } from "~/components/ui/social-link";
import { PlanBadge } from "~/components/plan-badge";
import { type CoachSelect, MembershipPlan } from "~/lib/schemas";
import { CDNImage } from "~/components/ui/cdn-image";
import { CoachProfileContext } from "~/contexts/coach";
import { getDB } from "~/lib/db";
import { getStripe } from "~/lib/stripe";
import { PreGeneratedProfileBanner } from "~/components/ui/pre-generated-profile-banner";
import { Tab, TabList } from "~/components/ui/tabs";
import * as v from "valibot";
import { upsertContact } from "~/lib/ghl/upsert-contact";
import { getLocationAccessToken } from "~/lib/ghl/get-location-access-token";
import { formAction$, valiForm$ } from "@modular-forms/qwik";
import { getCoachByUsername } from "~/lib/coach.db";
import { resources } from "~/../drizzle/schema";
import { count, eq } from "drizzle-orm";

// Action to create checkout session
export const useCreateCheckout = routeAction$(
  async (input, { sharedMap }) => {
    const coach: CoachSelect = sharedMap.get("coach");

    const offer = await getDB().query.offers.findFirst({
      where: (offers, { eq }) => eq(offers.id, input.offerId),
    });

    if (!offer || !offer.directSaleEnabled) {
      return {
        error: "Offer not found or not available for direct purchase",
      };
    }

    if (!coach.stripeAccountId || !coach.stripeChargesEnabled) {
      return {
        error: "Coach is not set up for payments",
      };
    }

    const stripe = getStripe();
    const baseUrl = import.meta.env.VITE_APP_URL;
    if (!baseUrl) {
      return {
        error: "VITE_APP_URL is not set",
      };
    }

    try {
      // Create a Stripe Checkout Session
      const session = await stripe.checkout.sessions.create(
        {
          mode: "payment",
          line_items: [
            {
              price_data: {
                currency: offer.currency!.toLowerCase(),
                product_data: {
                  name: offer.headline || "Coaching Offer",
                  description: offer.description || undefined,
                },
                unit_amount: offer.price!,
              },
              quantity: 1,
            },
          ],
          payment_intent_data: {
            application_fee_amount: Math.round(
              offer.price! * import.meta.env.VITE_APPLICATION_FEE_RATIO,
            ),
            metadata: {
              type: "direct_offer_purchase",
              coachId: coach.id,
              offerId: offer.id,
            },
          },
          success_url: `${baseUrl}/${coach.username}/offer?success=true`,
          cancel_url: `${baseUrl}/${coach.username}/offer`,
          customer_creation: "always",
          invoice_creation: {
            enabled: true,
          },
          metadata: {
            type: "direct_offer_purchase",
            coachId: coach.id,
            offerId: offer.id,
          },
        },
        { stripeAccount: coach.stripeAccountId },
      );

      return {
        success: true,
        checkoutUrl: session.url!,
      };
    } catch (error) {
      console.error("Failed to create checkout session:", error);
      return {
        error: "Failed to create checkout session",
      };
    }
  },
  zod$({
    offerId: z.string(),
  }),
);

export const LeadMagnetFormSchema = v.object({
  firstName: v.pipe(v.string(), v.minLength(1)),
  email: v.pipe(v.string(), v.email()),
});

export type LeadMagnetForm = v.InferOutput<typeof LeadMagnetFormSchema>;

export const useSubmitLeadAction = formAction$<LeadMagnetForm, { url: string }>(
  async ({ firstName, email }, { sharedMap, env }) => {
    const coach: CoachSelect | undefined = sharedMap.get("coach");
    if (!coach) {
      throw new Error("Coach not found");
    }

    if (!coach.ghlLocationId) {
      throw new Error("Coach does not have location id set");
    }

    const accessToken = await getLocationAccessToken(coach.ghlLocationId, env);

    await upsertContact(
      {
        firstName,
        email,
        locationId: coach.ghlLocationId,
        tags: ["source/dcm-lead"],
      },
      accessToken,
    );

    const profile = await getDB().query.profiles.findFirst({
      where: (profile, { eq }) => eq(profile.coachId, coach.id),
      columns: {
        leadMagnetUrl: true,
      },
    });

    if (!profile?.leadMagnetUrl) {
      return {
        status: "error",
        message: "Lead magnet URL not found",
      };
    }

    return {
      status: "success",
      data: {
        url: profile.leadMagnetUrl,
      },
    };
  },
  valiForm$(LeadMagnetFormSchema),
);

export const useResourceCountLoader = routeLoader$(async (requestEvent) => {
  const username = requestEvent.params.username;
  const coach = await getCoachByUsername(username);

  if (!coach) {
    // Or handle error appropriately, maybe return 0 or throw
    return 0;
  }

  const db = getDB();
  const result = await db
    .select({ value: count() })
    .from(resources)
    .where(eq(resources.coachId, coach.id));

  return result[0]?.value || 0;
});

interface ProfileLayoutProps {
  routePath?: string;
}

// Shared layout component that can be used in both routes
export const ProfileLayout = component$<ProfileLayoutProps>(
  ({ routePath = "" }) => {
    const coachSignal = useContext(CoachProfileContext);
    const coach = coachSignal.value;
    const resourceCount = useResourceCountLoader();

    const hasTabs =
      hasFeature(coach, Feature.Resources) ||
      hasFeature(coach, Feature.OfferPage);

    return (
      <>
        <div class="mx-auto my-4 flex max-w-5xl flex-col px-4 md:mt-8">
          {coach.profile.isPreGeneratedProfile && (
            <PreGeneratedProfileBanner
              coachName={coach.firstName}
              fullName={coach.fullName}
              username={coach.username}
            />
          )}
          {hasFeature(coach, Feature.FancyProfile) &&
            coach.profile.heroImageSrc && (
              <CDNImage
                src={coach.profile.heroImageSrc}
                width={1024}
                height={240}
                class="mb-8 h-60 w-full overflow-hidden rounded-2xl object-cover md:mb-12"
                alt=""
              />
            )}

          <div class="mb-4 flex w-full flex-col gap-8 sm:flex-row">
            <div class="flex-basis-0 flex-grow-2">
              <CoachProfileImage
                coach={coach}
                profilePictureUrl={coach.profilePictureUrl}
                fullName={coach.fullName}
              />

              {hasFeature(coach, Feature.FancyProfile) &&
                coach.profile.brandLogoUrl && (
                  <div class="mt-4 flex items-center gap-2 px-4">
                    <CDNImage
                      src={coach.profile.brandLogoUrl}
                      width={48}
                      height={48}
                      alt={`${coach.profile.brandName || coach.fullName}'s brand logo`}
                      class="h-12 w-12 rounded-full border object-cover"
                    />
                    {coach.profile.brandName && (
                      <div class="text-primary font-medium">
                        {coach.profile.brandName}
                      </div>
                    )}
                  </div>
                )}
            </div>

            <div class="flex-basis-0 flex-grow-6 flex flex-col items-stretch justify-center gap-8">
              <div class="flex flex-col items-center justify-between gap-4 sm:items-start md:flex-row">
                <div class="flex flex-col items-center gap-0 text-center sm:items-start sm:text-left">
                  <a
                    href={`/${coach.username}`}
                    class="ring-brand-100 flex items-center gap-2 rounded-lg ring-offset-2 transition hover:ring"
                  >
                    <h1 class="font-heading text-primary text-4xl">
                      {coach.fullName}
                    </h1>
                    {coach.membershipPlan !== MembershipPlan.Standard && (
                      <PlanBadge membershipPlan={coach.membershipPlan} />
                    )}
                  </a>
                  <div class="text-secondary mb-4 flex items-center gap-2 text-xl">
                    {coach.profile.coachingTitle}
                  </div>
                  {coach.profile.openingStatement && (
                    <div class="text-tertiary text-sm">
                      <LuQuote class="mr-2 inline-block rotate-180 text-gray-200" />
                      {coach.profile.openingStatement}
                      <LuQuote class="ml-2 inline-block rotate-0 text-gray-200" />
                    </div>
                  )}
                </div>

                {coach.ghlCalendarId && (
                  <div class="flex shrink-0 flex-row items-center justify-end gap-2">
                    <Link href={`/${coach.username}/book`}>
                      <Button
                        variant={
                          hasFeature(coach, Feature.FancyProfile)
                            ? "fancy"
                            : "default"
                        }
                      >
                        {coach.profile.ctaButtonText}
                      </Button>
                    </Link>
                  </div>
                )}
              </div>

              {hasTabs && (
                <TabList>
                  <Tab staticLink href={`${routePath}/${coach.username}`}>
                    About
                  </Tab>
                  {hasFeature(coach, Feature.Resources) &&
                    resourceCount.value > 0 && (
                      <Tab
                        staticLink
                        href={`${routePath}/${coach.username}/resources`}
                      >
                        Resources
                      </Tab>
                    )}
                  {hasFeature(coach, Feature.OfferPage) && (
                    <Tab
                      staticLink
                      href={`${routePath}/${coach.username}/offer`}
                    >
                      Offer
                    </Tab>
                  )}
                </TabList>
              )}
            </div>
          </div>

          <div class="flex w-full flex-col flex-col-reverse gap-8 md:flex-row">
            <div class="flex-basis-0 flex-grow-2">
              <CoachDetails
                coach={coach}
                profile={coach.profile}
                details={coach.details}
              />

              <div class="flex items-center justify-center gap-2 pt-4">
                <SocialLink
                  href={`https://dreamcoachmatch.com/${coach.username}`}
                  icon="dcm"
                  ariaLabel="Dream Coach Match Profile"
                />
                {coach.profile.website && (
                  <SocialLink
                    href={coach.profile.website}
                    icon="globe"
                    ariaLabel="Website"
                  />
                )}
                {coach.profile.facebookProfile && (
                  <SocialLink
                    href={coach.profile.facebookProfile}
                    prefix="https://facebook.com/"
                    icon="facebook"
                    ariaLabel="Facebook"
                  />
                )}
                {coach.profile.instagramProfile && (
                  <SocialLink
                    href={coach.profile.instagramProfile}
                    prefix="https://instagram.com/"
                    icon="instagram"
                    ariaLabel="Instagram"
                  />
                )}
                {coach.profile.linkedinProfile && (
                  <SocialLink
                    href={coach.profile.linkedinProfile}
                    prefix="https://linkedin.com/in/"
                    icon="linkedin"
                    ariaLabel="LinkedIn"
                  />
                )}
                {coach.profile.youtubeChannel && (
                  <SocialLink
                    href={coach.profile.youtubeChannel}
                    prefix="https://www.youtube.com/"
                    icon="youtube"
                    ariaLabel="YouTube"
                  />
                )}
              </div>
            </div>

            <div class="flex-basis-0 flex-grow-6 flex flex-col items-stretch gap-8">
              <Slot />
            </div>
          </div>
        </div>

        {hasFeature(coach, Feature.FancyProfile) && <GradientBackground />}
      </>
    );
  },
);

// Default export that uses the ProfileLayout
export default component$(() => {
  return (
    <>
      <ProfileLayout>
        <Slot />
      </ProfileLayout>
    </>
  );
});
